import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createNewsletter,
  deleteNewsletter,
  fetchNewsletterAdmin,
  updateNewsletter,
} from '../newsletterApi';
import type {
  NewsletterUpsertRequest,
} from '../newsletter.types';

export const NEWSLETTER_ADMIN_QUERY_KEY = ['newsletter', 'admin'] as const;
export const NEWSLETTER_PUBLIC_QUERY_KEY = ['newsletter', 'public'] as const;

export const useNewsletterAdmin = () => {
  const queryClient = useQueryClient();

  const invalidateNewsletters = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: NEWSLETTER_ADMIN_QUERY_KEY,
      }),
      queryClient.invalidateQueries({
        queryKey: NEWSLETTER_PUBLIC_QUERY_KEY,
      }),
    ]);
  };

  const adminQuery = useQuery({
    queryKey: NEWSLETTER_ADMIN_QUERY_KEY,
    queryFn: async () => {
      const response = await fetchNewsletterAdmin();
      return response.data;
    },
  });

  const createNewsletterMutation = useMutation({
    mutationFn: (payload: NewsletterUpsertRequest) => createNewsletter(payload),
    onSuccess: invalidateNewsletters,
  });

  const updateNewsletterMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: NewsletterUpsertRequest;
    }) => updateNewsletter(id, payload),
    onSuccess: invalidateNewsletters,
  });

  const deleteNewsletterMutation = useMutation({
    mutationFn: (id: string) => deleteNewsletter(id),
    onSuccess: invalidateNewsletters,
  });

  return {
    createNewsletter: createNewsletterMutation.mutateAsync,
    deleteNewsletter: deleteNewsletterMutation.mutateAsync,
    isCreatingNewsletter: createNewsletterMutation.isPending,
    isDeletingNewsletter: deleteNewsletterMutation.isPending,
    isLoading: adminQuery.isLoading,
    isUpdatingNewsletter: updateNewsletterMutation.isPending,
    pages: adminQuery.data ?? [],
    refreshNewsletters: invalidateNewsletters,
    updateNewsletter: updateNewsletterMutation.mutateAsync,
  };
};
